import { createContext, useContext, useEffect, useState } from "react";
import { analytics } from "@heycatch/sdk";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Best-effort HeyCatch calls — never throw into the auth flow.
const hcSafe = (fn) => { try { fn(); } catch { /* HeyCatch offline */ } };
const trackAuth = (event, user) => {
  hcSafe(() => {
    if (user?.id) analytics.identify?.(user.id, {
      email: user.email,
      plan: user.plan || "free",
      role: user.role || "user",
    });
    analytics.track?.(event, {
      plan: user?.plan || "free",
      has_code: !!user?.subscription_status && user.subscription_status !== "free",
    });
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Rehydrate HeyCatch identity on every session — otherwise refreshes
      // arrive as anonymous and the funnel loses attribution.
      hcSafe(() => data?.id && analytics.identify?.(data.id, {
        email: data.email,
        plan: data.plan || "free",
        role: data.role || "user",
      }));
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from Emergent Google Auth (URL fragment contains
    // session_id), skip the /auth/me check — AuthCallback will exchange the
    // session_id and establish the JWT cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    trackAuth("logged_in", data);
    return data;
  };

  const register = async (email, password, name, code, age_confirmed = false) => {
    const ref = typeof window !== "undefined" ? (localStorage.getItem("lectura_ref") || "") : "";
    const { data } = await api.post("/auth/register", { email, password, name, code, ref, age_confirmed });
    if (ref) { try { localStorage.removeItem("lectura_ref"); } catch { /* noop */ } }
    setUser(data);
    trackAuth("registered", data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setUser(false);
    hcSafe(() => analytics.reset?.());
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
